import React, { useEffect, useState } from 'react';
import Option from '../../../interfaces/Option';
import { useAuth } from '../../../auth/AuthProvider';
import { Box, Card, CardBody, CardHeader, Heading } from '@chakra-ui/react';
import InputSelect from '../../parts/InputSelect';
import ReserveScoutCensus from './scoutCensus/reserve/ReserveScoutCensus';
import VacationScoutCensus from './scoutCensus/vacation/VacationScoutCensus';
import { Census, RegistrationStateType } from '../../../model/census';
import { autoRefresh } from '../../helper/AutoRefreshHelper';
import LoadingWidget from '../../parts/LoadingWidget';
import { Scout } from '../../../model/scout';
import {useApiProvider} from "../../../api/ApiProvider";
import {ActiveScoutCensus} from "./scoutCensus/active/ActiveScoutCensus";
import CensusRenderComponent from "../../parts/CensusRenderComponent";
import {CensusComment} from "../../../model/censusComment";
import RegistrationState from '../../../utils/RegistrationStates';

const ScoutCensus: React.FC = () => {
    const { user } = useAuth();
    const provider = useApiProvider();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [census, setCensus] = useState<Census>();
    const [areCommentsFetched, setAreCommentsFetched] = useState<boolean>(false);
    const [comments, setComments] = useState<CensusComment[]>();

    const fetchData = async () => {
        provider.getCensus()
            .then(census => setCensus(census))
            .catch(err => console.error(err))
            .finally(() => setIsFetched(true));
    }

    useEffect(() => {
        if (census == undefined) return
        provider.getComments()
            .then(comment => setComments(comment))
            .catch(err => console.error(err))
            .finally(() => setAreCommentsFetched(true));
    }, [isFetched]);

    useEffect(() => autoRefresh(() => fetchData()), []);

    return <Box
        minHeight="100vh"
        width="100vw"
        backgroundImage="url('/images/cone.jpg')"
        backgroundSize="cover"
        backgroundPosition="center"
        display="flex"
        justifyContent="center"
        alignItems="center"
    >
        {
            isFetched ? (
                (
                    census && areCommentsFetched
                ) ? (<CensusExist census={census} comments={comments}/>) : (<CheckScoutDataForShowCensus census={census} />)
            ) : <LoadingWidget withImage={true} image="url('/images/cone.jpg')" />
        }
    </Box>
};

interface CheckScoutDataForShowCensusProps {
    census: Census | undefined;
}


function CheckScoutDataForShowCensus(props: CheckScoutDataForShowCensusProps) {
    const { user } = useAuth();
    const provider = useApiProvider();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [scout, setScout] = useState<Scout | undefined>();
    
    const fetchData = async () => {
        provider.getScout()
            .then(scout => setScout(scout))
            .catch(err => console.error(err))
            .finally(() => setIsFetched(true));
    }

    useEffect(() => autoRefresh(() => fetchData()), []);

    if (isFetched) {
        if (scout == undefined) {
            return <NoScoutData />
        } else {
            return <CensusForm census={props.census}/>
        }
    } else {
        return <LoadingWidget withImage={true} image="url('/images/cone.jpg')" />
    }
}

interface CensusExistProps {
    census: Census;
    comments?: CensusComment[];
}

const CensusExist: React.FC<CensusExistProps> = (props) => {
    return (
        <Box
            display="flex"
            alignItems="start"
            justifyContent="center"
            height="100%"
            width="100%"
            pt="89px"
        >
            <Card
                display="flex"
                justifyContent="center"
                alignItems="center"
                shadow="none"
                padding="10"
                width="100%"
                maxWidth="700px"
            >
                <CensusRenderComponent census={props.census} comments={props.comments} />
            </Card>
        </Box>
    )
};


function NoScoutData() {
    return (
        <Box
            display="flex"
            alignItems="start"
            justifyContent="center"
            height="100%"
            width="100%"
            pt="89px"
        >
            <Card
                display="flex"
                justifyContent="center"
                alignItems="center"
                shadow="none"
                padding="10"
                width="100%"
                maxWidth="500px"
            >
                <CardHeader>
                    <Heading size='md'>Rejestracja w bazie instruktorów</Heading>
                </CardHeader>
                <CardBody
                    width="100%"
                >
                    Brak danych harcerskich. Uzupełnij dane harcerskie i powróć do tego formularza.
                </CardBody>
            </Card>
        </Box>
    )
};


interface CensusFormProps {
    census: Census | undefined;
}

function CensusForm(props: CensusFormProps) {
    const requestOptions: Option[] = [
        { label: 'wpis na listę czynnych instruktorów', value: 'Active' },
        { label: 'przeniesienie do rezerwy', value: 'Reserve' },
        { label: 'urlop', value: 'Vacations' }
    ];
    const [selectedRequestOption, setSelectedRequestOption] = useState<Option | undefined>(undefined);

    return (
        <Box
            bg="white"
            mt="89px"
            padding="5"
            borderRadius="md"
            boxShadow="lg"
            textAlign="center"
            width="2xl"
            marginTop="120px"
            marginBottom="20px"
        >
            <Heading
                padding="3"
                as="h2"
                size="lg"
            >
                Rejestracja w bazie instruktorów
            </Heading>
            <Box padding="3">
                <Box display="grid">
                    <Box>
                        <InputSelect
                            initValue={undefined}
                            label={'Wniosek'}
                            placeholderText={'Wpisz o co wnioskujesz:'}
                            options={requestOptions}
                            isRequired={true}
                            getSelectedOption={setSelectedRequestOption}
                        />
                        {['Vacations'].includes(selectedRequestOption?.value!) && (
                            <VacationScoutCensus census={props.census}></VacationScoutCensus>
                        )}
                        {['Reserve'].includes(selectedRequestOption?.value!) && (
                            <ReserveScoutCensus census={props.census}></ReserveScoutCensus>
                        )}
                        {['Active'].includes(selectedRequestOption?.value!) && (
                            <ActiveScoutCensus census={props.census}></ActiveScoutCensus>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default ScoutCensus;
